import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getContractors,
  deleteContractor,
  toggleContractorStatus,
  clearContractorsError,
  clearContractorsSuccess,
} from "../../../../redux/Slices/contractorsSlice";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import SortOrderSelect from "../../../../shared/components/SortOrderSelect";
import { sortListById } from "../../../../shared/utils.js/utils";
const Contractors = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    list: contractors,
    isLoading,
    error,
    success,
    pagination: contractorsPagination,
  } = useSelector(state => state.contractors);
  const { t, i18n } = useTranslation("global");
  const { setTitle } = useTitle();
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractorToDelete, setContractorToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("asc");
  const pageSize = 15;
  const lastErrorRef = useRef({ message: null, time: 0 });
  // Use a ref to hold the latest search term to avoid re-creating fetchContractors callback
  const searchTermRef = useRef("");
  const searchDebounceRef = useRef(null);
  const currentPageRef = useRef(1);
  const sortOrderRef = useRef("asc");

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    sortOrderRef.current = sortOrder;
  }, [sortOrder]);

  useEffect(() => {
    setTitle("sidenav.hr.contractors");
  }, [setTitle]);

  const fetchContractors = useCallback(
    (
      page = currentPageRef.current,
      search = searchTermRef.current,
      order = sortOrderRef.current,
    ) => {
      const params = {
        page,
        per_page: pageSize,
        sort_by: "id",
        sort_order: order,
      };
      if (search?.trim()) {
        params.search = search.trim();
      }
      dispatch(getContractors(params));
    },
    [dispatch, pageSize],
  );

  useEffect(() => {
    fetchContractors(currentPage, searchTermRef.current, sortOrder);
  }, [currentPage, sortOrder, fetchContractors]);

  useEffect(() => {
    if (searchTerm === searchTermRef.current) return undefined;

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchTermRef.current = searchTerm;
      if (currentPageRef.current !== 1) {
        setCurrentPage(1);
      } else {
        fetchContractors(1, searchTerm, sortOrderRef.current);
      }
    }, 500);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm, fetchContractors]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearContractorsSuccess());
      fetchContractors();
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearContractorsError()), 3000);
    }
  }, [success, error, dispatch, isRTL, fetchContractors]);

  const handleAdd = () => {
    navigate("/contractors/add");
  };

  const handleEdit = id => {
    navigate(`/contractors/edit/${id}`);
  };

  const handleView = id => {
    navigate(`/contractors/${id}`);
  };

  const handleDelete = contractor => {
    setContractorToDelete(contractor);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (contractorToDelete) {
      await dispatch(deleteContractor(contractorToDelete.id));
      setShowDeleteModal(false);
      setContractorToDelete(null);
    }
  };

  const handleToggleStatus = async id => {
    await dispatch(toggleContractorStatus(id));
    // Refresh current page after status change
    fetchContractors();
  };

  const contractorsList = Array.isArray(contractors)
    ? contractors
    : Array.isArray(contractors?.data)
      ? contractors.data
      : Array.isArray(contractors?.data?.data)
        ? contractors.data.data
        : [];
  const sortedContractorsList = sortListById(contractorsList, sortOrder);

  // Get pagination info from API
  const pagination = contractorsPagination || {};
  const totalPages = pagination.last_page || 1;
  const currentPageSafe = pagination.current_page || currentPage || 1;
  const from = pagination.from || 1;
  const total = pagination.total || sortedContractorsList.length || 0;

  // Show pagination if we have data or pagination info
  const shouldShowPagination =
    sortedContractorsList.length > 0 &&
    (totalPages > 0 || contractorsPagination !== null);

  const getContractorName = contractor => {
    return isRTL
      ? contractor.name_ar || contractor.name_en
      : contractor.name_en || contractor.name_ar;
  };

  // Removed full-page isLoading return to prevent losing focus on search

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      <div style={{ marginBottom: "30px" }}>
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            marginBottom: "20px",
            color: "white",
            textAlign,
          }}
        >
          {t("contractors.title")}
        </h1>

        <div
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ position: "relative", flex: "1", minWidth: "300px" }}>
            <FaSearch
              style={{
                position: "absolute",
                [isRTL ? "left" : "right"]: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666",
                fontSize: "16px",
                zIndex: 1,
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder={t("contractors.searchPlaceholder")}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: isRTL ? "12px 45px 12px 15px" : "12px 15px 12px 45px",
                backgroundColor: "#1b3350",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                direction: dir,
                textAlign,
                boxSizing: "border-box",
              }}
            />
          </div>

          <button
            onClick={handleAdd}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaPlus />
            {t("contractors.add")}
          </button>
          <SortOrderSelect
            value={sortOrder}
            onChange={value => {
              setSortOrder(value);
              setCurrentPage(1);
              fetchContractors(1, searchTerm, value);
            }}
            style={{ minWidth: 220 }}
          />
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #333",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "1000px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "1px solid #333",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("contractors.table.number")}
                </th>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "1px solid #333",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("contractors.table.name")}
                </th>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "1px solid #333",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("contractors.table.taxNumber")}
                </th>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "1px solid #333",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("contractors.table.phone")}
                </th>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "1px solid #333",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("contractors.table.email")}
                </th>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "1px solid #333",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("contractors.table.status")}
                </th>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "1px solid #333",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("contractors.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && sortedContractorsList.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#bbb",
                    }}
                  >
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{ width: "1.5rem", height: "1.5rem" }}
                    >
                      <span className="visually-hidden">
                        {t("contractors.loading")}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : sortedContractorsList.length > 0 ? (
                sortedContractorsList.map((contractor, index) => (
                  <tr
                    key={contractor.id}
                    style={{
                      borderBottom: "1px solid #333",
                    }}
                  >
                    <td
                      style={{
                        padding: "15px",
                        color: "white",
                        fontWeight: "500",
                        textAlign: "center",
                      }}
                    >
                      {from + index}
                    </td>
                    <td
                      style={{
                        padding: "15px",
                        color: "white",
                        fontWeight: "500",
                        textAlign: "center",
                      }}
                    >
                      {getContractorName(contractor)}
                    </td>
                    <td
                      style={{
                        padding: "15px",
                        color: "white",
                        opacity: 0.9,
                        textAlign: "center",
                      }}
                    >
                      {contractor.tax_number || "-"}
                    </td>
                    <td
                      style={{
                        padding: "15px",
                        color: "white",
                        opacity: 0.9,
                        textAlign: "center",
                      }}
                    >
                      {contractor.phone || "-"}
                    </td>
                    <td
                      style={{
                        padding: "15px",
                        color: "white",
                        opacity: 0.9,
                        textAlign: "center",
                      }}
                    >
                      {contractor.email || "-"}
                    </td>
                    <td
                      style={{
                        padding: "15px",
                        textAlign: "center",
                      }}
                    >
                      <span
                        style={{
                          padding: "4px 12px",
                          borderRadius: "20px",
                          backgroundColor: contractor.is_active
                            ? "rgba(39,174,96,0.15)"
                            : "rgba(192,57,43,0.2)",
                          color: contractor.is_active ? "#2ecc71" : "#e74c3c",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}
                      >
                        {contractor.is_active
                          ? t("contractors.details.active")
                          : t("contractors.details.inactive")}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "15px",
                        textAlign: "center",
                        minWidth: "200px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                          flexWrap: "nowrap",
                        }}
                      >
                        <ActionButton
                          title={t("btns.view", { defaultValue: "عرض" })}
                          onClick={() => handleView(contractor.id)}
                        >
                          <FaEye />
                        </ActionButton>
                        <ActionButton
                          title={t("btns.edit", { defaultValue: "تعديل" })}
                          onClick={() => handleEdit(contractor.id)}
                        >
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title={t("btns.delete", { defaultValue: "حذف" })}
                          bg="#c0392b"
                          onClick={() => handleDelete(contractor)}
                        >
                          <FaTrash />
                        </ActionButton>
                        <ActionButton
                          title={
                            contractor.is_active
                              ? t("contractors.deactivate")
                              : t("contractors.activate")
                          }
                          bg="#34495e"
                          onClick={() => handleToggleStatus(contractor.id)}
                        >
                          {contractor.is_active ? (
                            <FaToggleOn />
                          ) : (
                            <FaToggleOff />
                          )}
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      color: "white",
                      padding: "20px",
                    }}
                  >
                    {t("contractors.noData")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {shouldShowPagination &&
          (() => {
            const pages = [];
            for (let i = 1; i <= totalPages; i++) {
              if (
                i === 1 ||
                i === totalPages ||
                (i >= currentPageSafe - 2 && i <= currentPageSafe + 2)
              ) {
                pages.push(i);
              } else if (
                i === currentPageSafe - 3 ||
                i === currentPageSafe + 3
              ) {
                pages.push("...");
              }
            }
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginTop: "20px",
                  paddingBottom: "20px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={() =>
                    setCurrentPage(Math.max(1, currentPageSafe - 1))
                  }
                  disabled={currentPageSafe === 1}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: currentPageSafe === 1 ? "#333" : "#AC2000",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: currentPageSafe === 1 ? "not-allowed" : "pointer",
                    opacity: currentPageSafe === 1 ? 0.5 : 1,
                  }}
                >
                  {t("contractors.pagination.previous")}
                </button>
                {pages.map((p, idx) =>
                  p === "..." ? (
                    <span
                      key={`ellipsis-${idx}`}
                      style={{ padding: "8px", color: "white" }}
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      style={{
                        padding: "8px 12px",
                        backgroundColor:
                          p === currentPageSafe ? "#AC2000" : "#1a1f2e",
                        color: "white",
                        border: "1px solid #333",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                  }
                  disabled={currentPageSafe === totalPages}
                  style={{
                    padding: "8px 12px",
                    backgroundColor:
                      currentPageSafe === totalPages ? "#333" : "#AC2000",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor:
                      currentPageSafe === totalPages
                        ? "not-allowed"
                        : "pointer",
                    opacity: currentPageSafe === totalPages ? 0.5 : 1,
                  }}
                >
                  {t("contractors.pagination.next")}
                </button>
              </div>
            );
          })()}
      </div>

      {showDeleteModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{
                backgroundColor: "#1b3350",
                border: "1px solid #333",
                borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              }}
            >
              <div
                className="modal-header"
                style={{ border: "none", borderBottom: "1px solid #333" }}
              >
                <h5 className="modal-title text-white" style={{ textAlign }}>
                  {t("contractors.delete.title")}
                </h5>
              </div>
              <div className="modal-body text-white" style={{ textAlign }}>
                <p>
                  {t("contractors.delete.message")} "
                  {getContractorName(contractorToDelete)}"؟
                </p>
                <p style={{ color: "white" }}>
                  {t("contractors.delete.irreversible")}
                </p>
              </div>
              <div className="modal-footer" style={{ border: "none" }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    backgroundColor: "#6c757d",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 20px",
                  }}
                >
                  {t("contractors.delete.cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  style={{
                    backgroundColor: "#dc3545",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 20px",
                  }}
                >
                  {t("contractors.delete.confirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ActionButton = ({ children, onClick, title, bg = "#2f3644" }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      backgroundColor: bg,
      color: "white",
      border: "none",
      borderRadius: "6px",
      padding: "8px 10px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "38px",
    }}
  >
    {children}
  </button>
);

export default Contractors;
